import { useEffect, useState } from 'react';

import { FormControl, FormControlLabel, FormLabel, Radio, RadioGroup } from '@mui/material';

import Input from '^/components/Input';
import { ConfirmDialogPopup } from '^/components/Popup/ConfirmDialogPopup/ConfirmDialogPopup';
import { CancellationReasonType, cancellationReasons } from '^/types/CancellationReasonType';
import { formatKRWWonAmount } from '^/util/formatKRWWonAmount';
import { MobxInputValue } from '^/util/MobxInputValue';

const ConfirmReservationCancelPopup = ({
  visible,
  handleCancel,
  handleConfirm,
  isMobile,
  cancelFee,
  cancelPolicy,
  refundPrice,
}: {
  visible: MobxInputValue<boolean>;
  handleCancel: () => void;
  handleConfirm: (cancelReason: string) => void;
  isMobile: boolean;
  cancelFee?: number;
  refundPrice?: number;
  cancelPolicy?: string;
}) => {
  const [isDisabled, setIsDisabled] = useState<boolean>(true);
  const [selectedReason, setSelectedReason] = useState<CancellationReasonType>('');
  const [otherReason, setOtherReason] = useState<string>('');

  const isOtherReason = selectedReason === '기타';

  useEffect(() => {
    if (!isOtherReason && selectedReason !== '') {
      setIsDisabled(false);
    } else if (isOtherReason && otherReason.replaceAll(' ', '') !== '') {
      setIsDisabled(false);
    } else {
      setIsDisabled(true);
    }
  }, [selectedReason, otherReason]);

  return (
    <ConfirmDialogPopup
      visible={visible}
      title={
        <div className="flex flex-col gap-[20px] w-full">
          <h1>정말 예약을 취소하겠습니까?</h1>
          {!!cancelFee && cancelFee > 0 && (
            <div className="flex flex-col gap-[10px]">
              <p className="whitespace-pre-line">
                {`취소 수수료가 발생하며, 취소는 즉시 처리됩니다.\n환불은 취소 수수료를 제외한 금액으로 영업일에 순차 진행되며, 카드사를 통해 2~5일 내 환불될 예정입니다.`}
              </p>
              <div>
                {typeof refundPrice === 'number' && (
                  <p>・ 환불 금액 : {formatKRWWonAmount(refundPrice)}원</p>
                )}
                <p>
                  ・ 취소 수수료 :{' '}
                  <span className="text-[#EF4444]">{formatKRWWonAmount(cancelFee)}원</span>
                </p>
                <p>・ 사유 : {cancelPolicy}</p>
              </div>
            </div>
          )}
          <FormControl fullWidth>
            <FormLabel id="CacellationReason::FormLabel">
              <p className="mb-[8px] text-black font-bold">
                <span className="mr-[4px] text-[#EF4444]">*</span>
                예약 취소 사유
              </p>
            </FormLabel>
            <RadioGroup
              value={selectedReason}
              onChange={(e) => {
                setSelectedReason(e.target.value);
                setOtherReason('');
              }}
            >
              {cancellationReasons.map((reason) => (
                <div className="flex w-full items-center" key={reason}>
                  <FormControlLabel value={reason} control={<Radio />} label={reason} />
                  {reason === '기타' && (
                    <Input
                      ref={null}
                      {...{
                        containerClassNames: 'flex-1',
                        classNames: 'h-[10px]',
                        height: '10px',
                        placeholder: '내용을 입력해주세요',
                        isDisabled: !isOtherReason,
                        focused: isOtherReason,
                        value: isOtherReason ? otherReason : '',
                        onChange: (e) => {
                          setOtherReason(e.target.value);
                        },
                        noMargin: true,
                      }}
                    />
                  )}
                </div>
              ))}
            </RadioGroup>
          </FormControl>
        </div>
      }
      confirmText="예약 취소"
      isDisabled={isDisabled}
      handleCancel={() => {
        setSelectedReason('');
        setOtherReason('');
        handleCancel();
      }}
      handleConfirm={() => {
        handleConfirm(isOtherReason ? `${selectedReason} [${otherReason}]` : selectedReason);
      }}
      isMobile={isMobile}
    />
  );
};

export default ConfirmReservationCancelPopup;
